import React, { useState, useEffect, useCallback } from 'react';
import {
  Phone, Loader2, AlertCircle, ArrowUpRight, ArrowDownLeft,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const token = () => sessionStorage.getItem('auth_token');

const PERIODS = [
  { key: 'today', label: 'Vandaag' },
  { key: 'week',  label: 'Week' },
  { key: 'month', label: 'Maand' },
  { key: 'all',   label: 'Alles' },
];

const PAGE_SIZE = 25;

function formatDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('nl-NL', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatDuration(seconds) {
  if (seconds == null) return '—';
  const s = Math.max(0, Math.round(seconds));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

function truncate(text, max = 90) {
  if (!text) return '';
  return text.length > max ? text.slice(0, max).trimEnd() + '…' : text;
}

export default function CallsPage() {
  const { user } = useAuth();
  // Zelfde set als de backend (_SEE_ALL_ROLES): deze rollen zien alle calls en mogen filteren op gebruiker
  const canFilterUsers = ['admin_pay', 'admin_trade', 'finance', 'backoffice'].includes(user?.role) || !!user?.is_teamleader;

  const [period, setPeriod] = useState('week');
  const [userFilter, setUserFilter] = useState('');
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ calls: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Gebruikers voor de filter-dropdown (zelfde endpoint als andere pagina's)
  useEffect(() => {
    if (!canFilterUsers) return;
    fetch('/api/v1/users/team', { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => (r.ok ? r.json() : []))
      .then(d => setUsers(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [canFilterUsers]);

  const fetchCalls = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        period,
        page: String(page),
        page_size: String(PAGE_SIZE),
      });
      if (userFilter) params.set('user_id', userFilter);
      const res = await fetch(`/api/v1/calls/?${params}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData({ calls: json.calls || [], total: json.total || 0 });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [period, userFilter, page]);

  useEffect(() => { fetchCalls(); }, [fetchCalls]);

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  return (
    <div className="h-screen flex flex-col bg-[#f7f8fc]">
      {/* Header */}
      <div className="bg-white border-b border-[#e8eaf2] px-8 py-5">
        <h1 className="text-2xl font-bold mb-3" style={{ color: '#011745' }}>Calls</h1>
        <div className="flex items-center gap-4 flex-wrap">
          {/* Periode-pills */}
          <div className="flex gap-1 bg-[#f3f4f8] rounded-lg p-1 w-fit">
            {PERIODS.map(p => (
              <button
                key={p.key}
                onClick={() => { setPeriod(p.key); setPage(1); }}
                className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                  period === p.key ? 'bg-white shadow-sm' : 'hover:bg-white/50'
                }`}
                style={{ color: period === p.key ? '#3d61a4' : '#7b859e' }}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Gebruikersfilter (admin/teamleider) */}
          {canFilterUsers && (
            <select
              value={userFilter}
              onChange={e => { setUserFilter(e.target.value); setPage(1); }}
              className="text-sm rounded-lg px-3 py-2 border focus:outline-none focus:ring-1"
              style={{ borderColor: '#e8eaf2', color: '#3d61a4', fontWeight: 600, backgroundColor: '#eef2fa' }}
            >
              <option value="">Alle gebruikers</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.full_name}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={32} className="animate-spin" style={{ color: '#3d61a4' }} />
          </div>
        ) : error ? (
          <div className="bg-red-50 text-red-600 rounded-xl p-4 flex items-center gap-3">
            <AlertCircle size={20} /><span className="text-sm">{error}</span>
          </div>
        ) : data.calls.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-[#e8eaf2]">
            <Phone size={40} className="mx-auto mb-3" style={{ color: '#cdd1e0' }} />
            <p className="font-semibold" style={{ color: '#011745' }}>Nog geen gesprekken</p>
            <p className="text-sm mt-1" style={{ color: '#7b859e' }}>
              Zodra de telefoonprovider gekoppeld is verschijnen calls hier automatisch.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-[#e8eaf2] shadow-sm">
            <div className="px-6 py-4 border-b border-[#e8eaf2] flex items-center justify-between">
              <h3 className="text-sm font-bold" style={{ color: '#011745' }}>
                Telefoongesprekken ({data.total})
              </h3>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#e8eaf2]">
                  <th className="text-left text-xs font-semibold uppercase px-6 py-3" style={{ color: '#7b859e' }}>Datum / tijd</th>
                  <th className="text-left text-xs font-semibold uppercase px-6 py-3" style={{ color: '#7b859e' }}>Bedrijf</th>
                  <th className="text-left text-xs font-semibold uppercase px-6 py-3" style={{ color: '#7b859e' }}>Gebruiker</th>
                  <th className="text-left text-xs font-semibold uppercase px-6 py-3" style={{ color: '#7b859e' }}>Richting</th>
                  <th className="text-left text-xs font-semibold uppercase px-6 py-3" style={{ color: '#7b859e' }}>Duur</th>
                  <th className="text-left text-xs font-semibold uppercase px-6 py-3" style={{ color: '#7b859e' }}>Uitkomst</th>
                  <th className="text-left text-xs font-semibold uppercase px-6 py-3" style={{ color: '#7b859e' }}>Samenvatting</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#f3f4f8]">
                {data.calls.map(call => {
                  const summary = call.ai_summary || call.summary || '';
                  return (
                    <tr key={call.id} className="hover:bg-[#f7f8fc] transition-colors">
                      <td className="px-6 py-3 text-sm whitespace-nowrap" style={{ color: '#566079' }}>
                        {formatDateTime(call.occurred_at)}
                      </td>
                      <td className="px-6 py-3 text-sm font-semibold" style={{ color: '#011745' }}>
                        {call.company_name || '—'}
                      </td>
                      <td className="px-6 py-3 text-sm" style={{ color: '#566079' }}>
                        {call.user_name || '—'}
                      </td>
                      <td className="px-6 py-3">
                        {call.direction === 'outbound' ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg"
                            style={{ backgroundColor: '#eef2fa', color: '#3d61a4' }}>
                            <ArrowUpRight size={12} /> Uit
                          </span>
                        ) : call.direction === 'inbound' ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg"
                            style={{ backgroundColor: '#f0fdf4', color: '#16a34a' }}>
                            <ArrowDownLeft size={12} /> In
                          </span>
                        ) : (
                          <span className="text-xs" style={{ color: '#a4abbe' }}>—</span>
                        )}
                      </td>
                      <td className="px-6 py-3 text-sm whitespace-nowrap" style={{ color: '#566079' }}>
                        {formatDuration(call.duration_seconds)}
                      </td>
                      <td className="px-6 py-3 text-sm" style={{ color: '#566079' }}>
                        {call.outcome || '—'}
                      </td>
                      <td className="px-6 py-3 text-sm max-w-md" style={{ color: '#566079' }} title={summary}>
                        {truncate(summary) || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Paginering */}
            <div className="px-6 py-4 border-t border-[#e8eaf2] flex items-center justify-between">
              <p className="text-xs" style={{ color: '#7b859e' }}>
                Pagina {page} van {totalPages} · {data.total} gesprek{data.total !== 1 ? 'ken' : ''}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all disabled:opacity-40"
                  style={{ backgroundColor: '#eef2fa', color: '#3d61a4' }}
                >
                  <ChevronLeft size={13} /> Vorige
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all disabled:opacity-40"
                  style={{ backgroundColor: '#eef2fa', color: '#3d61a4' }}
                >
                  Volgende <ChevronRight size={13} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
